import { useRef, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, FileStack } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Props {
  companyId: string;
  onUploaded: () => void;
  disabled?: boolean;
}

const MAX_FILES_PER_BATCH = 10;
const MAX_BYTES = 20 * 1024 * 1024;

/**
 * Unified multi-file upload zone. User drops/picks 1..N PDFs and each gets
 * inserted as a separate `company_verification_documents` row with
 * doc_type='unknown'. The parser then classifies + extracts and updates
 * doc_type on the row.
 */
export function MultiFileUploadZone({ companyId, onUploaded, disabled }: Props) {
  const { toast } = useToast();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    if (!user) return;
    const list = Array.from(files).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (list.length === 0) {
      toast({ title: 'PDF files only', variant: 'destructive' });
      return;
    }
    if (list.length > MAX_FILES_PER_BATCH) {
      toast({ title: `Too many files`, description: `Max ${MAX_FILES_PER_BATCH} per upload.`, variant: 'destructive' });
      return;
    }
    const oversize = list.find((f) => f.size > MAX_BYTES);
    if (oversize) {
      toast({ title: 'File too large', description: `${oversize.name} exceeds 20MB.`, variant: 'destructive' });
      return;
    }

    setBusy(true);
    let success = 0;
    let failed = 0;

    await Promise.all(list.map(async (file) => {
      try {
        const ext = file.name.split('.').pop() || 'pdf';
        const path = `${companyId}/unknown/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('compliance-documents')
          .upload(path, file, { upsert: false, contentType: file.type || 'application/pdf' });
        if (upErr) throw new Error(upErr.message);

        const { data: inserted, error: insErr } = await supabase
          .from('company_verification_documents' as any)
          .insert({
            company_id: companyId,
            uploaded_by: user.id,
            doc_type: 'unknown',
            storage_path: path,
            file_name: file.name,
            file_size: file.size,
            mime_type: file.type || 'application/pdf',
            review_status: 'pending_parse',
          } as any)
          .select('id')
          .single();
        if (insErr || !inserted) throw new Error(insErr?.message || 'Insert failed');

        // Fire & forget — parser will classify and update doc_type.
        supabase.functions.invoke('parse-verification-document', {
          body: { document_id: (inserted as any).id },
        }).catch(() => {});

        success++;
      } catch (e: any) {
        failed++;
        console.error('[MultiFileUploadZone]', file.name, e?.message);
      }
    }));

    // Grant posting rights once (idempotent).
    if (success > 0) {
      await supabase.rpc('mark_company_documents_submitted' as any, { _company_id: companyId });
    }

    toast({
      title: success > 0 ? `${success} file${success > 1 ? 's' : ''} uploaded` : 'Upload failed',
      description: failed > 0 ? `${failed} file${failed > 1 ? 's' : ''} failed.` : 'AI is identifying each document.',
      variant: failed > 0 && success === 0 ? 'destructive' : 'default',
    });

    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    onUploaded();
  }, [companyId, user, toast, onUploaded]);

  return (
    <Card
      className={`p-6 border-2 border-dashed transition-colors ${dragOver ? 'border-accent bg-accent/5' : 'border-border'}`}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled || busy) return;
        handleFiles(e.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => e.target.files && handleFiles(e.target.files)}
        disabled={disabled || busy}
      />
      <div className="flex flex-col items-center text-center gap-2">
        <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
          {busy ? <Loader2 className="h-5 w-5 text-accent animate-spin" /> : <FileStack className="h-5 w-5 text-accent" />}
        </div>
        <h3 className="text-sm font-bold text-foreground">
          {busy ? 'Uploading…' : 'Drop your compliance documents here'}
        </h3>
        <p className="text-xs text-muted-foreground max-w-md">
          We'll detect each document type automatically. PDF only · 20 MB per file · up to {MAX_FILES_PER_BATCH} files at once.
        </p>
        <Button
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
          className="mt-1"
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          Choose files
        </Button>
      </div>
    </Card>
  );
}
